---
title: "Migrating from Zoho Bigin to NerdCRM: the 30-minute checklist"
description: "Step-by-step migration from Bigin to NerdCRM: export contacts, companies, deals; map fields; preserve owner assignment; verify before cutting over."
seoTitle: "Migrate from Zoho Bigin to NerdCRM in 30 minutes"
publishedAt: "2026-06-14"
slug: "migrating-from-zoho-bigin"
authorPhoto: "/og/sohel-headshot.jpg"
authorSameAs:
  - { label: "LinkedIn", url: "https://www.linkedin.com/in/mdsohelrana/" }
---

# Migrating from Zoho Bigin to NerdCRM: the 30-minute checklist

If you've decided to switch from Bigin to NerdCRM (or you're considering it), here's the actual checklist. We've helped a handful of customers through this in the last few months and the same script works each time. Plan for 30–60 minutes the first time; teams that have done it once can run it in 20.

## Before you start

- Decide on a cutover time when nobody is in the middle of a deal close. End of week is usually best.
- Tell the team. The Kanban will look different the next morning.
- Pick the NerdCRM org that will receive the data — usually a fresh one (Settings → Create new org).

## Step 1 — Export from Bigin

In Bigin: **Setup → Data Administration → Export Data**

Export, in this order:

1. **Companies** — produces `Accounts.csv`
2. **Contacts** — produces `Contacts.csv`
3. **Deals (Pipelines)** — produces `Pipelines.csv`
4. **Activities** — produces `Tasks.csv`, `Calls.csv`, `Events.csv`

Save them all to one folder. They're plain CSVs.

## Step 2 — Sketch your pipeline

Bigin pipelines and NerdCRM pipelines look the same; the stage names just need mapping. In NerdCRM, **Pipelines → New pipeline** and recreate your stage names.

Your `Pipelines.csv` has a `Stage` column with the Bigin stage names — note them down. The most common Bigin defaults map to:

- `Qualification` → `Qualified`
- `Needs Analysis` → `Callback scheduled`
- `Value Proposition` → `In discussion`
- `Negotiation` → `In discussion`
- `Closed Won` → `Won`
- `Closed Lost` → handled via the deal status field, not a stage

## Step 3 — Import companies first

NerdCRM's CSV importer is at **Settings → Import → Companies**.

Drop `Accounts.csv` in. Map:

- Bigin `Account Name` → NerdCRM `Name`
- Bigin `Website` → NerdCRM `Domain`
- Bigin `Industry` → NerdCRM `Industry`
- Bigin `Description` → NerdCRM `Description`

Skip everything else for round one — you can backfill notes later.

## Step 4 — Import contacts

**Settings → Import → Contacts**, drop `Contacts.csv` in. Map:

- Bigin `First Name` / `Last Name` → NerdCRM same
- Bigin `Email` → NerdCRM `Email` (primary)
- Bigin `Phone` / `Mobile` → NerdCRM `Phone` (primary)
- Bigin `Account Name` → NerdCRM `Company` — this auto-matches against the companies you just imported
- Bigin `Owner` → NerdCRM `Owner` — see step 6 about owner mapping

## Step 5 — Import deals

**Settings → Import → Deals**, drop `Pipelines.csv` in.

The mapping that matters:

- Bigin `Stage` → NerdCRM `Stage` — pick from your new pipeline's stages
- Bigin `Amount` → NerdCRM `Value` (currency from a column or a fixed default)
- Bigin `Closing Date` → NerdCRM `Expected close date`
- Bigin `Account Name` → NerdCRM `Company`
- Bigin `Contact Name` → NerdCRM `Contacts`
- Bigin `Owner` → NerdCRM `Owner`

For deals that were `Closed Won` or `Closed Lost` in Bigin, set the deal status accordingly during import — there's a column-mapping option.

## Step 6 — Owners

Bigin's CSV exports have owner emails, not user IDs. Before importing, create the matching users in NerdCRM (**Settings → Users → Invite**) using the same email addresses. The importer will match by email.

For users you don't want to recreate (e.g., people who left the team), assign their deals to a "house" owner during import.

## Step 7 — Verify

Spot-check before declaring victory:

- 10 random contacts: phone format normalized? company linked?
- 5 random open deals: stage correct? owner correct? value correct?
- One won deal and one lost deal — status correct?
- The KAM report (Reports → KAM report) — do the per-rep totals match Bigin's?

## Step 8 — Cut over

- Tell the team Bigin is read-only as of this morning
- Update softphones, email signatures, anything pointing at Bigin URLs
- Start placing new deals in NerdCRM

Keep the Bigin account active for one billing cycle as a backup. Cancel after you've made it through one full deal cycle (typically 2–6 weeks for most small teams).

## Activities

Bigin's calls, tasks, and events are also CSV-exportable, but the formats differ. We don't have a one-click importer for them today (see the [help center](/help) for the workaround). For most teams, the cleanest move is to start the activity log fresh in NerdCRM and treat the historical activity as archived in Bigin.

## Need help?

We help the first cohort of customers migrate in person. If you're moving more than a few hundred records, email hello@nerddevs.com — we'll do a 30-minute screen-share walkthrough at no cost.
